import { defineStore } from 'pinia'
import { filter, asLink, asHTML } from '@prismicio/client';
import sanitizeHtml from 'sanitize-html'

import { usePrismicClient } from '~/composables/usePrismicClient'
import type { PageType, PageUrlType } from '@/types'

export const usePageStore = defineStore('page', () => {
  const { client } = usePrismicClient()
  const { setLoading } = useUIStore()

  // page is kept as a ref so Pinia serializes it in the SSR payload.
  // pageCache holds data per URL so client-side navigation can switch instantly.
  const page = ref<PageType | null>()
  const pageCache = ref<Record<string, PageType | null>>({})

  const parsePage = (doc: Record<string, any>): PageType | undefined => {
    try {
      const title = asHTML(doc.title) || ''
      const subtitle = asHTML(doc.subtitle) || ''
      return {
        title,
        subtitle,
        assetUrl: asLink(doc.asset_url) || '',
        content: asHTML(doc.content) || '',
        seo: {
          title: doc.seo_title || sanitizeHtml(title, { allowedTags: [] }),
          metaDescription: doc.seo_meta_description || sanitizeHtml(subtitle, { allowedTags: [] }),
          keywords: doc.seo_keywords?.split(','),
          socialAssetUrl: asLink(doc.seo_social_asset_url) || '',
        }
      }
    } catch (error) {
      console.error('Error parsing page data from Prismic:', error)
    }
  }

  const fetchPage = async (value: PageUrlType | string): Promise<PageType | undefined> => {
    try {
      setLoading(true)
      const response = await client.get({
        filters: [
          filter.at('document.type', 'page'),
          filter.at('my.page.page_url', value)
        ]
      })

      const filteredPage = response.results[0]

      const parsedResponse = filteredPage?.data === undefined ? undefined : parsePage(filteredPage?.data)

      return Promise.resolve(parsedResponse)
    } catch (error) {
      console.error('Error fetching page data from Prismic:', error)
      return Promise.resolve(undefined)
    } finally {
      setLoading(false)
    }
  }

  // Called in onBeforeMount of each page component to switch page instantly from
  // cache on client navigation (useAsyncData is cached from SSR and won't re-run).
  const setCurrentUrl = (url: string) => {
    if (pageCache.value[url] !== undefined) {
      page.value = pageCache.value[url]
    }
  }

  const loadPage = async (value: PageUrlType | string): Promise<boolean> => {
    try {
      if (pageCache.value[value] !== undefined) {
        page.value = pageCache.value[value]
        return Promise.resolve(true)
      }
      const data = await fetchPage(value) ?? null
      pageCache.value[value] = data
      page.value = data
      return Promise.resolve(true)
    } catch (error) {
      console.error('Error loading page data from Prismic:', error)
      return Promise.resolve(false)
    }
  }

  return {
    page,
    pageCache,

    loadPage,
    fetchPage,
    setCurrentUrl,
  }
})
